import type { Chat, ChatMessage, ChatUser } from '../model/schema';

/**
 * Who a message is from and how it renders (cpanel `MessageItem`). The wire
 * `ChatMessage` only carries `outgoing`; the role/actor lookups below also
 * accept the alternative actor fields cpanel probed (`sender`, `user`, …) so a
 * richer realtime payload keeps resolving to the right party.
 */

/** The first value that is neither nullish nor an empty string. */
export function firstFilled(
  ...values: (string | null | undefined)[]
): string | undefined {
  return values.find((value): value is string => value != null && value !== '');
}

/** Roles that speak FOR the company — their messages sit on the right. */
const ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN', 'CALL_CENTER', 'OPERATOR'] as const;

/** Which side of the thread a speaker is on. */
export type MessageParty = 'operator' | 'courier' | 'vendor' | 'client';

type ActorShape = Partial<ChatUser> & { full_name?: string | null };

/** A message plus the loose actor fields a realtime payload may carry. */
type ActorCarrier = ChatMessage & {
  sender?: ActorShape | null;
  user?: ActorShape | null;
  author?: ActorShape | null;
  from_user?: ActorShape | null;
  created_by?: ActorShape | null;
  role?: string | null;
};

function readActor(message: ActorCarrier): ActorShape | undefined {
  return (
    message.sender ??
    message.user ??
    message.author ??
    message.from_user ??
    message.created_by ??
    undefined
  );
}

/** Upper-cased role of the speaker; falls back to the chat owner's role. */
export function getMessageRole(message: ChatMessage, chat: Chat | undefined): string {
  const carrier = message as ActorCarrier;
  const role = firstFilled(
    carrier.sender?.role,
    carrier.user?.role,
    carrier.author?.role,
    carrier.from_user?.role,
    carrier.created_by?.role,
    carrier.role,
    message.outgoing ? 'ADMIN' : chat?.user?.role,
  );
  return (role ?? '').toUpperCase();
}

/** Operator-side message: outgoing, or sent by an admin-ish role. */
export function isOperatorMessage(message: ChatMessage, chat: Chat | undefined): boolean {
  const role = getMessageRole(message, chat);
  return message.outgoing || (ADMIN_ROLES as readonly string[]).includes(role);
}

/** The party that sent the message, for avatar tint + fallback name. */
export function messageParty(message: ChatMessage, chat: Chat | undefined): MessageParty {
  if (isOperatorMessage(message, chat)) return 'operator';
  const role = getMessageRole(message, chat);
  if (role === 'COURIER') return 'courier';
  if (role === 'VENDOR') return 'vendor';
  return 'client';
}

/**
 * The person behind a message: an explicit actor with a name wins, otherwise
 * the chat's responsible operator (operator side) or its owner (other side).
 */
export function messageActor(
  message: ChatMessage,
  chat: Chat | undefined,
  operatorSide: boolean,
): ActorShape | undefined {
  const actor = readActor(message);
  if (actor?.first_name || actor?.last_name || actor?.full_name) return actor;
  return operatorSide ? (chat?.responsible ?? undefined) : (chat?.user ?? undefined);
}

/** Display name of an actor, or `fallback` when none is known. */
export function actorName(actor: ActorShape | undefined, fallback: string): string {
  return (
    firstFilled(
      actor?.full_name,
      [actor?.first_name, actor?.last_name].filter(Boolean).join(' '),
    ) ?? fallback
  );
}

/** Up to two initials of a name (`fallback` when the name is blank). */
export function getInitials(name: string, fallback: string): string {
  return (firstFilled(name) ?? fallback)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * Initials of a chat owner for the avatar: first letters of first + last name,
 * `U`/`N` placeholders when absent (cpanel list/header avatar).
 */
export function chatOwnerInitials(user: ChatUser | null | undefined): string {
  return `${firstFilled(user?.first_name?.charAt(0)) ?? 'U'}${firstFilled(user?.last_name?.charAt(0)) ?? 'N'}`;
}

/** Full display name of a chat owner (cpanel header: `first last`). */
export function chatOwnerName(
  user: ChatUser | null | undefined,
  fallback: string,
): string {
  return (
    firstFilled([user?.first_name, user?.last_name].filter(Boolean).join(' ')) ?? fallback
  );
}

/** i18n key suffix (`inbox:role.*`) of a chat owner's backend role. */
export function chatRoleKey(
  role: string | null | undefined,
): 'courier' | 'restaurant' | 'client' {
  switch (String(role ?? '').toUpperCase()) {
    case 'COURIER':
      return 'courier';
    case 'VENDOR':
      return 'restaurant';
    default:
      return 'client';
  }
}
